import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Typography from '@mui/material/Typography';

import { IReservation } from '../adapters/reservationsAdapter';
import { IEmployee } from '../adapters/employeesAdapter';
import { IPropertyMgmtOrg } from '../adapters/propertyMgmtOrgsAdapter';
import useTranslator from '../../../utilities/hooks/useTranslator';
import { parseApiDate } from '../../../utilities/apiDates';
import CommonCardContent from './CommonCardContent';
import LoadingState from '../../../components/LoadingState';
import ConfirmDialog from './ConfirmDialog';
import StartDialog from './StartDialog';
import CommonActionMenu from './CommonActionMenu';
import StopDialog from './StopDialog';
import CompleteDialog from './CompleteDialog';
import RemoveDialog from './RemoveDialog';
import RestartDialog from './RestartDialog';

export interface IToConfirmTabContentProps {
    allReservations: IReservation[] | undefined,
    allEmployees: IEmployee[] | undefined,
    propertyMgmtOrg: IPropertyMgmtOrg | undefined,
    safeSetViewLoadingState: (loadingState: LoadingState) => void,
    refreshSingleReservationInList: (reservation: IReservation) => void,
    safeSetUserMessageContent: (messageContent: string) => void
}

const ToConfirmTabContentDict = {
    waitingForConfirmation: { en: 'Waiting for confirmation', fr: 'En attente de confirmation' },
    confirmButton: { en: 'Confirm', fr: 'Confirmer' },
    ToConfirmNoReservationMsg: { en: 'There is no cleaning job to confirm.', fr: 'Aucune job d\'entretien à confirmer.' }
};

const ToConfirmTabContent = (props: IToConfirmTabContentProps) => {

    const translate = useTranslator();

    const [confirmDlgOpenInfo, setConfirmDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);
    const [startDlgOpenInfo, setStartDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);
    const [stopDlgOpenInfo, setStopDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);
    const [completeDlgOpenInfo, setCompleteDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);
    const [restartDlgOpenInfo, setRestartDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);
    const [removeDlgOpenInfo, setRemoveDlgOpenInfo] = useState<{ open: boolean, reservation: IReservation } | undefined>(undefined);

    const [cardMenuOpen, setCardMenuOpen] = React.useState(false);
    const [cardMenuAnchorEl, setCardMenuAnchorEl] = React.useState<undefined | HTMLElement>(undefined);
    const [cardMenuReservation, setCardMenuReservation] = React.useState<undefined | IReservation>(undefined);

    const renderNow = new Date();

    const toBeConfirmedReservation = props.allReservations?.filter((value) => value.cleaningJob.status === 'ToBeConfirmed');

    toBeConfirmedReservation?.sort((a, b) => parseApiDate(a.startDate).getTime() - parseApiDate(b.startDate).getTime());

    const isInWarningState = (reservation: IReservation) =>
        ((parseApiDate(reservation.endDate).getTime() - renderNow.getTime()) / 1000 / 60 / 60) < (props.propertyMgmtOrg?.cleaningJobSettings.shouldHaveBeenConfirmedTimeOut ?? 0);

    const isInUrgentState = (reservation: IReservation) =>
        ((parseApiDate(reservation.endDate).getTime() - renderNow.getTime()) / 1000 / 60 / 60) < (props.propertyMgmtOrg?.cleaningJobSettings.mustHaveBeenConfirmedTimeOut ?? 0);

    const handleCardMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, reservation: IReservation) => {
        setCardMenuReservation(reservation)
        setCardMenuAnchorEl(event.currentTarget);
        setCardMenuOpen(true);
    };

    return !(props.allReservations && props.allEmployees && props.propertyMgmtOrg) ? null :
        <React.Fragment>
            {toBeConfirmedReservation?.map((reservation, index) => <Card key={reservation.id} sx={{ mb: 1 }} data-cy={'ToConfirmCard_' + index.toString()}>
                <CommonCardContent
                    reservation={reservation}
                    isInUrgentState={isInUrgentState(reservation)}
                    isInWarningState={isInWarningState(reservation)}
                    cardStateDescription={translate(ToConfirmTabContentDict.waitingForConfirmation)}
                    allEmployees={props.allEmployees as IEmployee[]} />
                <CardActions disableSpacing={true}>
                    <Button color='secondary' onClick={() => setConfirmDlgOpenInfo({ open: true, reservation: reservation })} data-cy='CardConfirmAction'>{translate(ToConfirmTabContentDict.confirmButton)}</Button>
                    <IconButton aria-label="more" sx={{ ml: 'auto' }} onClick={(event) => handleCardMenuOpen(event, reservation)} data-cy='CardOverflowMenu'>
                        <Icon>more_vert</Icon>
                    </IconButton>
                </CardActions>
            </Card>)
            }
            {toBeConfirmedReservation?.length === 0 && <Typography variant="body1" paragraph={true} color={(theme) => theme.palette.text.disabled} m={1} fontStyle={'italic'} data-cy='ToConfirmNoReservationMsg'>{translate(ToConfirmTabContentDict.ToConfirmNoReservationMsg)}</Typography>}
            <CommonActionMenu startActionHandler={(reservation) => setStartDlgOpenInfo({ open: true, reservation: reservation })}
                completeActionHandler={(reservation) => setCompleteDlgOpenInfo({ open: true, reservation: reservation })}
                stopActionHandler={(reservation) => setStopDlgOpenInfo({ open: true, reservation: reservation })}
                restartActionHandler={(reservation) => setRestartDlgOpenInfo({ open: true, reservation: reservation })}
                removeActionHandler={(reservation) => setRemoveDlgOpenInfo({ open: true, reservation: reservation })}
                closeMenuActionHandler={() => setCardMenuOpen(false)}
                open={cardMenuOpen}
                menuAnchorEl={cardMenuAnchorEl}
                reservation={cardMenuReservation} />
            <ConfirmDialog openInfo={confirmDlgOpenInfo}
                setOpenInfo={setConfirmDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
            <StartDialog openInfo={startDlgOpenInfo}
                setOpenInfo={setStartDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
            <StopDialog openInfo={stopDlgOpenInfo}
                setOpenInfo={setStopDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
            <CompleteDialog openInfo={completeDlgOpenInfo}
                setOpenInfo={setCompleteDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
            <RestartDialog openInfo={restartDlgOpenInfo}
                setOpenInfo={setRestartDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
            <RemoveDialog openInfo={removeDlgOpenInfo}
                setOpenInfo={setRemoveDlgOpenInfo}
                allEmployees={props.allEmployees}
                propertyMgmtOrg={props.propertyMgmtOrg}
                safeSetViewLoadingState={props.safeSetViewLoadingState}
                refreshSingleReservationInList={props.refreshSingleReservationInList}
                safeSetUserMessageContent={props.safeSetUserMessageContent} />
        </React.Fragment >;
};

export default ToConfirmTabContent